import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  FormControl,
  MenuItem,
  FormHelperText,
  Grid,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Input,
  CardMedia,
  Checkbox,
  FormGroup,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Save,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Clear,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import TextField from '../components/UIKit/TextField';
import Button from '../components/UIKit/Button';
import Select from '../components/UIKit/Select';
import Switch from '../components/UIKit/Switch';

import { learningPathsAPI, coursesAPI } from '../services/api';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { useAuth } from '../context/AuthContext';

const LearningPathForm = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = !!pathId;
  const { user } = useAuth();

  // Используем новую систему переводов с namespace
  const { t } = useTranslation(['common', 'learningPaths']);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner',
    image: '',
    professions: new Set(),
    isPublished: false,
    courses: [],
    mandatory: {
      enabled: false,
      forRoles: [],
      order: 0,
    },
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [availableCourses, setAvailableCourses] = useState([]);
  const [courseInput, setCourseInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [canEdit, setCanEdit] = useState(true);

  // Получаем профессии и состояние загрузки из Redux
  const availableProfessions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);

  // --- Helpers for IDs and mapping ---
  const toId = useCallback((val) => {
    if (val == null) return null;
    if (typeof val === 'object') {
      const raw = val.id ?? val._id;
      return raw == null ? null : +raw;
    }
    return +val;
  }, []);

  const idsFromProfessions = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return list.map(toId).filter((v) => v != null && !Number.isNaN(v));
  }, [toId]);

  const objectsFromIds = (ids, dict) => {
    if (!Array.isArray(ids) || !Array.isArray(dict)) return [];
    const idSet = new Set(ids.map((v) => +v));
    return dict.filter((p) => idSet.has(+((p.id ?? p._id))));
  };

  // Derive UI value from formData.professions (works for create/edit)
  const derivedSelectedProfessions = useMemo(() => {
    if (!formData?.professions || !availableProfessions?.length) return [];
    const ids = [...formData.professions].map((v) => +v).filter((v) => !Number.isNaN(v));
    return objectsFromIds(ids, availableProfessions);
  }, [formData?.professions, availableProfessions]);

  // Professions coming from selected courses
  const derivedCourseProfessionIds = useMemo(() => {
    const fromCourses = new Set();
    (formData.courses || []).forEach((ci) => {
      const list = ci?.course?.professions;
      if (Array.isArray(list)) {
        list.forEach((pid) => {
          const idNum = +(typeof pid === 'object' && pid !== null ? (pid.id ?? pid._id) : pid);
          if (!Number.isNaN(idNum)) fromCourses.add(idNum);
        });
      }
    });
    return [...fromCourses];
  }, [formData.courses]);

  // Missing professions: present in courses but not yet in path
  const missingProfessionIds = useMemo(() => {
    const inPath = new Set([...(formData.professions || [])].map((v) => +v));
    return derivedCourseProfessionIds.filter((id) => !inPath.has(+id));
  }, [derivedCourseProfessionIds, formData.professions]);

  const missingProfessionObjects = useMemo(() => {
    return objectsFromIds(missingProfessionIds, availableProfessions);
  }, [missingProfessionIds, availableProfessions]);

  const handleAddMissingProfession = useCallback((id) => {
    setFormData((prev) => {
      const next = new Set(prev.professions);
      next.add(+id);
      return { ...prev, professions: next };
    });
  }, []);

  const handleAddAllMissingProfessions = useCallback(() => {
    setFormData((prev) => {
      const next = new Set(prev.professions);
      missingProfessionIds.forEach((id) => next.add(+id));
      return { ...prev, professions: next };
    });
  }, [missingProfessionIds]);

  const fetchLearningPath = useCallback(async () => {
    try {
      setLoading(true);
      // Проверка ID перед запросом
      if (!pathId || pathId === 'undefined' || pathId === 'null') {
        throw new Error(t('learningPaths:error.invalidId'));
      }

      const response = await learningPathsAPI.getById(pathId);

      // Обработка новой структуры ответа
      let pathData;

      if (response && response.data && response.data.data) {
        // Новая структура с вложенными данными
        pathData = response.data.data;
      } else if (response && response.data) {
        // Старая структура с одним уровнем data
        pathData = response.data;
      } else {
        // Прямой ответ
        pathData = response;
      }

      if (!pathData) {
        throw new Error(t('learningPaths:error.noData'));
      }

      // Проверяем структуру ответа для курсов
      let coursesData = [];
      if (pathData.courses) {
        // Проверяем, что pathData.courses - это массив
        if (Array.isArray(pathData.courses)) {
          coursesData = pathData.courses;
        }
      }

      // Проверяем, что каждый элемент courses содержит объект course
      const validCourses = coursesData.filter(
        (course) => course && course.course
      );

      // Обработка массива профессий - отфильтровываем null-значения
      const validProfessions = Array.isArray(pathData.professions)
        ? pathData.professions.filter((prof) => prof !== null && prof !== undefined)
        : [];

      const professionsSet = new Set(
        validProfessions
          .map((prof) => (typeof prof === 'object' && prof !== null ? (prof.id ?? prof._id) : prof))
          .filter((id) => id !== null && id !== undefined)
          .map((id) => +id)
      );

      setFormData({
        title: pathData.title || '',
        description: pathData.description || '',
        difficulty: pathData.difficulty || 'beginner',
        image: pathData.image || '',
        professions: professionsSet,
        isPublished: pathData.isPublished || false,
        courses: validCourses,
        mandatory: pathData.mandatory || { enabled: false, forRoles: [], order: 0 },
      });

      // Если есть изображение, устанавливаем его в превью
      if (pathData.image) {
        setImagePreview(pathData.image);
      }

      // Проверяем, может ли пользователь редактировать этот Learning Path
      const isAuthor = pathData.createdBy && user?._id === (pathData.createdBy._id || pathData.createdBy);
      const isAdmin = user?.roles?.includes('admin');
      const isPublished = pathData.isPublished;
      
      // Логика разрешения редактирования:
      // Админы могут редактировать все, авторы - только неопубликованные
      const canEditPath = isAdmin || (isAuthor && !isPublished);
      setCanEdit(canEditPath);

      setLoading(false);
    } catch (err) {
      console.error(
        `${t('learningPaths:error.load')} ${pathId}:`,
        err
      );
      toast.error(t('learningPaths:error.load'));
      setLoading(false);
      navigate('/learning-paths');
    }
  }, [t, pathId, navigate, user]);

  const fetchAvailableCourses = useCallback(async () => {
    try {
      // Добавляем параметр limit=0, чтобы получить все курсы без пагинации
      const response = await coursesAPI.getAllShort();

      // Проверяем структуру ответа и извлекаем массив курсов
      let coursesData = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          // Если данные уже массив
          coursesData = response.data;
        } else if (
          response.data.courses &&
          Array.isArray(response.data.courses)
        ) {
          // Если курсы находятся в свойстве courses
          coursesData = response.data.courses;
        }
      }

      setAvailableCourses(coursesData);
    } catch (err) {
      console.error(t('learningPaths:error.coursesLoad'), err);
      toast.error(t('learningPaths:error.coursesLoad'));
      setAvailableCourses([]);
    }
  }, [t]);

  useEffect(() => {
    // Заменяем вызов fetchProfessions на диспатч Redux-действия
    if (isEditMode) {
      // Проверяем валидность ID дорожки
      if (!pathId || pathId === 'undefined' || pathId === 'null') {
        console.error(t('learningPaths:error.invalidId'), pathId);
        toast.error(t('learningPaths:error.invalidId'));
        navigate('/learning-paths');
        return;
      }

      fetchLearningPath();
    }
  }, [isEditMode, pathId, navigate, dispatch, t, fetchLearningPath]);
  
  useEffect(() => {
    fetchAvailableCourses();
  }, [fetchAvailableCourses]);

  useEffect(() => {
    dispatch(fetchProfessions({ all: true, isCommon:1 }));
  }, [dispatch]);

  // Обработчик изменения файла изображения
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Проверка типа файла
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('learningPaths:error.invalidImageType'));
      return;
    }

    // Проверка размера файла (5MB максимум)
    const maxSize = 5 * 1024 * 1024; // 5MB в байтах
    if (file.size > maxSize) {
      toast.error(t('learningPaths:error.imageTooLarge'));
      return;
    }

    setImageFile(file);

    // Создаем URL для предпросмотра изображения
    const previewURL = URL.createObjectURL(file);
    setImagePreview(previewURL);

    // Очищаем ошибку поля изображения, если она была
    if (errors.image) {
      setErrors({
        ...errors,
        image: '',
      });
    }
  };

  // Функция для удаления выбранного изображения
  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({
      ...formData,
      image: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Очищаем ошибку для поля при изменении
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  // Обработчик выбора профессий
  const handleProfessionsChange = useCallback((event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      professions: new Set(idsFromProfessions(newValue)),
    }));
  }, [idsFromProfessions]);

  const handleAddCourse = useCallback(async (newCourse) => {
    if (!newCourse) return;
    try {
      if (!newCourse._id) {
        toast.error(t('learningPaths:error.courseNoId'));
        return;
      }
      if (formData.courses.some((c) => c.course && c.course._id === newCourse._id)) {
        toast.warning(t('learningPaths:error.courseAlreadyAdded'));
        return;
      }
      // Обновляем через функциональный сеттер, нормализуем ID в числа
      setFormData((prev) => {
        const nextProf = new Set(prev.professions);
        if (Array.isArray(newCourse.professions)) {
          newCourse.professions.forEach((pid) => {
            const idNum = +(typeof pid === 'object' && pid !== null ? (pid.id ?? pid._id) : pid);
            if (!Number.isNaN(idNum)) nextProf.add(idNum);
          });
        }
        const nextCourses = [
          ...prev.courses,
          { course: newCourse, order: prev.courses.length },
        ];
        return { ...prev, courses: nextCourses, professions: nextProf };
      });
    } catch (err) {
      console.error(t('learningPaths:error.addCourse'), err);
      toast.error(t('learningPaths:error.addCourse'));
    }
  }, [formData, t]);

  const handleRemoveCourse = useCallback(async (courseId) => {
    if (!courseId) {
      console.error(t('learningPaths:error.courseIdNotProvided'));
      return;
    }
    try {
      setFormData((prev) => {
        const updatedCourses = prev.courses.filter((c) => !(c.course && c.course._id === courseId));
        const courseToRemove = prev.courses.find((c) => c.course && c.course._id === courseId);
        const nextProf = new Set([...prev.professions].map((v) => +v));
        if (courseToRemove?.course?.professions) {
          courseToRemove.course.professions.forEach((pid) => {
            const pidStr = (typeof pid === 'object' && pid !== null ? (pid.id ?? pid._id) : pid);
            const pidNum = +pidStr;
            if (Number.isNaN(pidNum)) return;
            const usedElsewhere = updatedCourses.some((ci) =>
              ci?.course?.professions &&
              Array.isArray(ci.course.professions) &&
              ci.course.professions.some((p2) => +((typeof p2 === 'object' && p2 !== null ? (p2.id ?? p2._id) : p2)) === pidNum)
            );
            if (!usedElsewhere) nextProf.delete(pidNum);
          });
        }
        return { ...prev, courses: updatedCourses, professions: nextProf };
      });
    } catch (err) {
      console.error(t('learningPaths:error.removeCourse'), err);
      toast.error(t('learningPaths:error.removeCourse'));
    }
  }, [t]);

  // Новые функции для перемещения курсов вверх/вниз
  const moveCourseUp = useCallback((index) => {
    if (index <= 0 || !formData.courses || !formData.courses.length) return;

    try {
      const updatedCourses = [...formData.courses];
      const temp = updatedCourses[index];
      updatedCourses[index] = updatedCourses[index - 1];
      updatedCourses[index - 1] = temp;

      // Обновляем order
      const coursesWithUpdatedOrder = updatedCourses.map((course, idx) => ({
        ...course,
        order: idx,
      }));

      setFormData((prevData) => ({
        ...prevData,
        courses: coursesWithUpdatedOrder,
      }));
    } catch (err) {
      console.error(t('learningPaths:error.moveUp'), err);
      toast.error(t('learningPaths:error.moveError'));
    }
  }, [formData.courses, t]);

  const moveCourseDown = useCallback((index) => {
    if (!formData.courses || index >= formData.courses.length - 1) return;

    try {
      const updatedCourses = [...formData.courses];
      const temp = updatedCourses[index];
      updatedCourses[index] = updatedCourses[index + 1];
      updatedCourses[index + 1] = temp;

      // Обновляем order
      const coursesWithUpdatedOrder = updatedCourses.map((course, idx) => ({
        ...course,
        order: idx,
      }));

      setFormData((prevData) => ({
        ...prevData,
        courses: coursesWithUpdatedOrder,
      }));
    } catch (err) {
      console.error(t('learningPaths:error.moveDown'), err);
      toast.error(t('learningPaths:error.moveError'));
    }
  }, [formData.courses, t]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('learningPaths:validation.titleRequired');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('learningPaths:validation.descriptionRequired');
    }

    if (!formData.difficulty) {
      newErrors.difficulty = t('learningPaths:validation.difficultyRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('learningPaths:error.fillRequiredFields'));
      return;
    }

    try {
      setLoading(true);

      // Фильтруем только действительные курсы перед отправкой
      const validCourses = formData.courses.filter(
        (course) => course && course.course && course.course._id
      );

      // Подготавливаем данные для отправки
      const pathData = {
        ...formData,
        // Для API нужно отправить только _id и order для каждого курса
        courses: validCourses.map((course) => ({
          course: course.course._id,
          order: course.order || 0,
        })),
        // Для API нужно отправить массив идентификаторов профессий
        professions: [...formData.professions],
      };

      // Если есть файл изображения, используем методы с загрузкой изображения
      if (imageFile) {
        if (isEditMode) {
          await learningPathsAPI.updateWithImage(
            pathId,
            pathData,
            imageFile
          );
          toast.success(t('learningPaths:success.pathUpdated'));
        } else {
          await learningPathsAPI.createWithImage(
            pathData,
            imageFile
          );
          toast.success(t('learningPaths:success.pathCreated'));
        }
      } else {
        // Иначе используем обычные методы
        if (isEditMode) {
          await learningPathsAPI.update(pathId, pathData);
          toast.success(t('learningPaths:success.pathUpdated'));
        } else {
          await learningPathsAPI.create(pathData);
          toast.success(t('learningPaths:success.pathCreated'));
        }
      }

      navigate('/learning-paths');
    } catch (err) {
      console.error(t('learningPaths:error.save'), err);
      toast.error(
        `${t('learningPaths:error.save')} ${
          err.message || t('common:errors.unknown')
        }`
      );
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Container sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Если пользователь не может редактировать опубликованный Learning Path
  if (isEditMode && !canEdit) {
    return (
      <Container sx={{ py: 4 }}>
        <Box
          mb={4}
          display="flex"
          flexWrap="wrap"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              component={Link}
              to="/learning-paths"
              sx={{ mr: 3 }}
            >
              {t('common:actions.back')}
            </Button>
            <Typography variant="h3" component="h1" fontWeight="bold">
              {t('learningPaths:form.title.edit')}
            </Typography>
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('learningPaths:error.cannotEditPublished')}
          </Typography>
          <Typography variant="body2">
            {t('learningPaths:form.restrictions.publishedEdit')}
          </Typography>
        </Alert>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            component={Link}
            to={`/learning-paths/${pathId}`}
          >
            {t('learningPaths:detail.viewPath')}
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/learning-paths"
          >
            {t('learningPaths:backToList')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box
        mb={4}
        display="flex"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            component={Link}
            to="/learning-paths"
            sx={{ mr: 3 }}
          >
            {t('common:actions.back')}
          </Button>
          <Typography variant="h3" component="h1" fontWeight="bold">
            {isEditMode
              ? t('learningPaths:form.title.edit')
              : t('learningPaths:form.title.create')}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={8}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ mb: 3 }}
              >
                {t('learningPaths:form.sections.basicInfo')}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label={t('learningPaths:form.fields.title')}
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    error={!!errors.title}
                    helperText={errors.title}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('learningPaths:form.fields.description')}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    error={!!errors.description}
                    helperText={errors.description}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.difficulty}>
                    <Select
                      name="difficulty"
                      value={formData.difficulty}
                      onChange={handleInputChange}
                      defaultValue="beginner"
                      label={t('learningPaths:form.fields.difficulty')}
                    >
                      <MenuItem value="beginner">
                        {t('common:difficulty.beginner')}
                      </MenuItem>
                      <MenuItem value="intermediate">
                        {t('common:difficulty.intermediate')}
                      </MenuItem>
                      <MenuItem value="advanced">
                        {t('common:difficulty.advanced')}
                      </MenuItem>
                    </Select>
                    {errors.difficulty && (
                      <FormHelperText>{errors.difficulty}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('learningPaths:form.fields.image')}
                    </Typography>

                    {imagePreview ? (
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        <CardMedia
                          component="img"
                          src={imagePreview}
                          alt={
                            formData.title ||
                            t('learningPaths:form.fields.imagePreview')
                          }
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                        />
                        <IconButton
                          onClick={handleImageRemove}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(0,0,0,0.7)',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: '2px dashed #ccc',
                          borderRadius: 1,
                          p: 2,
                          textAlign: 'center',
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                        component="label"
                      >
                        <Input
                          type="file"
                          onChange={handleImageChange}
                          sx={{ display: 'none' }}
                          inputProps={{
                            accept:
                              'image/jpeg, image/png, image/gif, image/webp',
                          }}
                        />
                        <CloudUpload
                          sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t('learningPaths:form.fields.uploadImage')}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" alignItems="center">
                      <TextField
                        label={t('learningPaths:form.fields.imageUrl')}
                        name="image"
                        value={formData.image}
                        onChange={handleInputChange}
                        fullWidth
                        placeholder={t(
                          'learningPaths:form.placeholders.imageUrl'
                        )}
                        disabled={!!imageFile}
                        helperText={
                          imageFile
                            ? t('learningPaths:form.fields.imageUploadSelected')
                            : ''
                        }
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: '16px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('learningPaths:form.sections.professions')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('learningPaths:form.descriptions.professions')}
              </Typography>

              <Autocomplete
                fullWidth
                multiple
                id="professions-select"
                options={professionsLoading ? [] : availableProfessions}
                getOptionLabel={(option) =>
                  option.term_group && option.term_group.MainTerm && option.term_group.MainTerm.value
                    ? option.term_group.MainTerm.value
                    : option.name || ''
                }
                value={derivedSelectedProfessions}
                onChange={handleProfessionsChange}
                isOptionEqualToValue={(option, value) => {
                  const optionId = (option.id ?? option._id);
                  const valueId = (value.id ?? value._id);
                  return optionId != null && valueId != null && optionId.toString() === valueId.toString();
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={
                        option.term_group && option.term_group.MainTerm && option.term_group.MainTerm.value
                          ? option.term_group.MainTerm.value
                          : option.name || ''
                      }
                      {...getTagProps({ index })}
                      key={option._id || option.id || index}
                      color="primary"
                      size="medium"
                      sx={{ m: 0.3 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('learningPaths:form.fields.selectProfession')}
                    placeholder={
                      derivedSelectedProfessions && derivedSelectedProfessions.length > 0
                        ? ''
                        : professionsLoading
                        ? t('common:loading')
                        : t('learningPaths:form.fields.selectProfession')
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {professionsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={professionsLoading}
                disableCloseOnSelect
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    flexWrap: 'wrap',
                  },
                }}
              />
              {missingProfessionObjects && missingProfessionObjects.length > 0 && (
                <Box mt={1.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">
                      {t('learningPaths:form.labels.missingProfessions')}
                    </Typography>
                    <Button size="small" variant="text" onClick={handleAddAllMissingProfessions}>
                      {t('learningPaths:form.actions.addAll')}
                    </Button>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {missingProfessionObjects.map((p) => (
                      <Chip
                        key={p._id || p.id}
                        label={
                          p.term_group && p.term_group.MainTerm && p.term_group.MainTerm.value
                            ? p.term_group.MainTerm.value
                            : p.name || String(p.id || p._id)
                        }
                        onClick={() => handleAddMissingProfession(p.id ?? p._id)}
                        clickable
                        color="primary"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} xl={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', mb: 3 }}>
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ mb: 3 }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {t('learningPaths:form.sections.publishSettings')}
                </Typography>
                {user?.roles?.includes('admin') && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPublished}
                        onChange={handleSwitchChange}
                        name="isPublished"
                        color="primary"
                        sx={{ mx: 1 }}
                      />
                    }
                    label={
                      formData.isPublished
                        ? t('common:status.published')
                        : t('common:status.draft')
                    }
                  />
                )}
              </Box>

              <Alert
                severity={formData.isPublished ? 'success' : 'info'}
                sx={{ mb: 3 }}
              >
                {formData.isPublished
                  ? t('learningPaths:form.alerts.published')
                  : t('learningPaths:form.alerts.draft')}
              </Alert>

              {user?.roles?.includes('admin') && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.mandatory.enabled}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mandatory: { ...formData.mandatory, enabled: e.target.checked },
                          })
                        }
                        color="warning"
                        sx={{ mx: 1 }}
                      />
                    }
                    label={t('learningPaths:form.mandatory.label')}
                    sx={{ mb: 2 }}
                  />
                  {formData.mandatory.enabled && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('learningPaths:form.mandatory.forRolesLabel')}
                      </Typography>
                      <FormGroup row>
                        {['user', 'student', 'teacher', 'admin'].map((role) => (
                          <FormControlLabel
                            key={role}
                            control={
                              <Checkbox
                                checked={formData.mandatory.forRoles.includes(role)}
                                onChange={(e) => {
                                  const newRoles = e.target.checked
                                    ? [...formData.mandatory.forRoles, role]
                                    : formData.mandatory.forRoles.filter((r) => r !== role);
                                  setFormData({
                                    ...formData,
                                    mandatory: { ...formData.mandatory, forRoles: newRoles },
                                  });
                                }}
                                size="small"
                              />
                            }
                            label={t(`learningPaths:form.mandatory.roles.${role}`)}
                          />
                        ))}
                      </FormGroup>
                      <TextField
                        label={t('learningPaths:form.mandatory.orderLabel')}
                        type="number"
                        value={formData.mandatory.order}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mandatory: { ...formData.mandatory, order: parseInt(e.target.value) || 0 },
                          })
                        }
                        inputProps={{ min: 0 }}
                        size="small"
                        sx={{ mt: 2, width: '120px' }}
                      />
                      {formData.mandatory.forRoles.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {t('learningPaths:form.mandatory.alertActive', {
                            roles: formData.mandatory.forRoles.join(', '),
                          })}
                        </Alert>
                      )}
                    </Box>
                  )}
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<Save />}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('learningPaths:form.actions.save')
                )}
              </Button>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: '16px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('learningPaths:form.sections.courses')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t('learningPaths:form.descriptions.courses')}
              </Typography>

              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                gap={1}
                sx={{ mb: 3 }}
              >
                <Autocomplete
                  options={
                    Array.isArray(availableCourses)
                      ? availableCourses.filter((course) => {
                          // Проверяем наличие course и его _id
                          if (!course || !course._id) return false;

                          // Проверяем, не добавлен ли уже этот курс
                          return !formData.courses.some(
                            (c) => c.course && c.course._id === course._id
                          );
                        })
                      : []
                  }
                  getOptionLabel={(option) => option?.title || ''}
                  inputValue={courseInput}
                  onInputChange={(event, newInputValue, reason) => {
                    // Keep user's typed text; ignore Autocomplete attempts to reset on select
                    if (reason === 'input' || reason === 'clear') {
                      setCourseInput(newInputValue || '');
                    }
                  }}
                  // Добавляем отображение ID курса для отладки
                  renderOption={(props, option) => {
                    const { key, ...liProps } = props;
                    const uniqueKey = option?._id || option?.id || key;
                    return (
                      <li key={uniqueKey} {...liProps}>
                        {option?.title || t('common:status.unknown')}
                        {option?._id && (
                          <span style={{ fontSize: '0.7em', color: '#888', marginLeft: '0.5em' }}>
                            ID: {option._id.substring(0, 8)}...
                          </span>
                        )}
                      </li>
                    );
                  }}
                  value={null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleAddCourse(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('learningPaths:form.fields.selectCourse')}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {courseInput ? (
                              <IconButton
                                size="small"
                                aria-label="clear"
                                onClick={() => setCourseInput('')}
                                sx={{ mr: 0.5 }}
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ flexGrow: 1 }}
                />
              </Box>

              {!formData.courses || formData.courses.length === 0 ? (
                <Alert severity="info">
                  {t('learningPaths:form.noCourses')}
                </Alert>
              ) : (
                <List sx={{ pl: 0 }}>
                  {(formData.courses || [])
                    .filter((courseItem) => courseItem && courseItem.course)
                    .map((courseItem, index) => {
                      if (!courseItem || !courseItem.course) return null;

                      const courseId = courseItem.course._id || `temp-${index}`;
                      const courseTitle =
                        courseItem.course.title || t('common:loading');

                      return (
                        <ListItem
                          sx={{
                            backgroundColor: 'background.default',
                            borderRadius: '12px',
                            marginBottom: '12px',
                            position: 'relative',
                            pr: { xs: '110px', sm: '120px' },
                            '& .MuiListItemSecondaryAction-root': {
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 'auto',
                              display: 'flex',
                              position: 'absolute',
                            },
                          }}
                          key={`course-${courseId}`}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                >
                                  {index + 1}.
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {courseTitle}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: 'block',
                                }}
                              >
                                ID: {courseId}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => moveCourseUp(index)}
                              disabled={index === 0}
                              sx={{ mr: 0.5 }}
                              title={t('common:actions.moveUp')}
                            >
                              <ArrowUpward />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveCourseDown(index)}
                              disabled={index === formData.courses.length - 1}
                              sx={{ mr: 0.5 }}
                              title={t('common:actions.moveDown')}
                            >
                              <ArrowDownward />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveCourse(courseId)}
                              size="small"
                              title={t('common:actions.delete')}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default LearningPathForm;
